import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface DeleteColumnResponse {
  success: boolean;
  message: string;
}

async function deleteColumn(columnId: string, token: string | undefined, workspaceId: string) {
  const bearer = resolveBearerToken(token);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const response = await axios.delete<DeleteColumnResponse>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/columns/${columnId}`,
    { headers },
  );

  return response.data;
}

export function useDeleteColumn() {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    // Try id first, then SK, then extract from SK if it starts with "WS#"
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      // If SK starts with "WS#", extract the UUID part
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3); // Remove "WS#" prefix
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);

  return useMutation({
    mutationFn: (columnId: string) => {
      console.log('🗑️ Deleting column:', columnId, 'from workspace:', workspaceId);
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      return deleteColumn(columnId, user?.signInUserSession?.idToken?.jwtToken, workspaceId);
    },
    onMutate: async (columnId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries([queryKeys.tickets, workspaceId]);

      // Snapshot the previous value
      const previousTickets = queryClient.getQueryData([queryKeys.tickets, workspaceId]);

      // Optimistically remove the column from cache
      queryClient.setQueryData([queryKeys.tickets, workspaceId], (old: any) => {
        if (!old) return old;

        console.log('🔮 Optimistic delete: Removing column', columnId);

        // Remove column from columns array
        const newColumns = old.columns.filter((c: any) => c.id !== columnId);

        // Remove tickets that were in this column
        const ticketsInColumn = old.columns.find((c: any) => c.id === columnId)?.ticketOrder || [];
        const newTickets = old.tickets.filter((t: any) => !ticketsInColumn.includes(t.ticketId));

        console.log(`🔮 Optimistic: Removed column ${columnId} and ${ticketsInColumn.length} tickets from UI`);
        return { ...old, columns: newColumns, tickets: newTickets };
      });

      // Return context with the snapshot
      return { previousTickets };
    },
    onSuccess: async (data, columnId) => {
      console.log('✅ Column deleted successfully:', columnId);
      console.log('🔄 Invalidating tickets query for workspace:', workspaceId);

      // Invalidate tickets query - React Query will refetch and sync with server
      await queryClient.invalidateQueries([queryKeys.tickets, workspaceId]);

      console.log('✅ Query invalidated, refetch will happen automatically');
    },
    onError: (error: any, columnId, context: any) => {
      console.error('❌ Column deletion failed:', error);

      // Rollback to previous state on error
      if (context?.previousTickets) {
        queryClient.setQueryData([queryKeys.tickets, workspaceId], context.previousTickets);
        console.log('🔙 Rolled back to previous state due to error');
      }

      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Unauthorized. Please log in again.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (error.response?.status === 404) {
        throw new Error('Column not found.');
      } else if (error.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(error.message || 'Failed to delete column. Please try again.');
      }
    },
  });
}
